import { createMtListenerMiddleware } from "@/lib/storage/create-mt-listener-middleware"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { TripSync } from "@/lib/trip-sync"
import { chatsApi } from "./api/chats-api"
import { mediaApi } from "./api/media-api"
import { updateTripCache } from "./api/trips-api"
import {
  removeEvents,
  removeEventsWithPlace,
} from "./itinerary-remove-events-thunks"
import { createEvent } from "./itinerary-thunks"
import tripSlice, { removeTrip, selectTripRef } from "./trip-slice"
import userSlice from "./user-slice"

const tripSyncMiddleware = createMtListenerMiddleware<RootState>(
  "trip-sync-middleware"
)

tripSyncMiddleware.startListening({
  actionCreator: createEvent.fulfilled,
  effect: (action) => {
    const { event, tripRef } = action.payload
    if (event?.place?.entity_ref) {
      TripSync.instance.broadcastPlaceAddedToTrip(
        event.place.entity_ref,
        tripRef
      )
    }
  },
})

tripSyncMiddleware.startListening({
  actionCreator: removeEvents.fulfilled,
  effect: (action) => {
    const { placeRefs, tripRef } = action.payload
    placeRefs.forEach((placeRef) => {
      TripSync.instance.broadcastPlaceRemovedFromTrip(placeRef, tripRef)
    })
  },
})

tripSyncMiddleware.startListening({
  actionCreator: removeEventsWithPlace.fulfilled,
  effect: (action) => {
    const { placeRefs, tripRef } = action.payload
    // Broadcast that these places were removed from the trip
    placeRefs.forEach((placeRef) => {
      TripSync.instance.broadcastPlaceRemovedFromTrip(placeRef, tripRef)
    })
  },
})

tripSyncMiddleware.startListening({
  actionCreator: tripSlice.actions.setTripRef,
  effect: (action, listenerApi) => {
    const previousState = listenerApi.getOriginalState()
    const newTripRef = action.payload
    const previousTripRef = previousState.tripSlice.tripRef

    // Only clear when switching to a different trip, not when clearing
    if (previousTripRef && newTripRef && previousTripRef !== newTripRef) {
      listenerApi.dispatch(
        userSlice.actions.setSelectedPlaceSearchParams(undefined)
      )
    }

    if (newTripRef && previousTripRef !== newTripRef) {
      TripSync.instance.broadcastTripCreated(newTripRef)
    }
  },
})

// Clear selectedPlaceSearchParams when tripRef changes via setChat
tripSyncMiddleware.startListening({
  actionCreator: userSlice.actions.setChat,
  effect: (action, listenerApi) => {
    const previousState = listenerApi.getOriginalState()
    const newTripRef = action.payload.trip?.ref ?? null
    const previousTripRef = previousState.tripSlice.tripRef

    // Only clear when switching to a different trip, not when clearing
    if (previousTripRef && newTripRef && previousTripRef !== newTripRef) {
      listenerApi.dispatch(
        userSlice.actions.setSelectedPlaceSearchParams(undefined)
      )
    }
  },
})

/** The trip on the streamed message: a `trip` chunk sets it, a `chat` chunk nests it. */
const selectStreamedTrip = (state: RootState) => {
  const message = state.userSlice.inProgressMessage
  return message?.trip || message?.chat?.trip
}

// Sync streaming trip updates to RTK Query cache
tripSyncMiddleware.startListening({
  // Every streamed chunk re-sends the message with the same chat and trip
  // objects (the builder keeps them by reference), so the cache is only
  // patched when a new trip reaches the store.
  predicate: (action, currentState, previousState) =>
    userSlice.actions.updateInProgressMessage.match(action) &&
    selectStreamedTrip(currentState) !== selectStreamedTrip(previousState),
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState()
    const tripUpdate = selectStreamedTrip(state)

    if (tripUpdate?.ref && selectTripRef(state) === tripUpdate.ref) {
      updateTripCache(
        listenerApi.dispatch as AppDispatch,
        tripUpdate.ref,
        tripUpdate,
        listenerApi.getState
      )
    }
  },
})

// Refresh the chat list once the bot has named the chat mid-stream
tripSyncMiddleware.startListening({
  predicate: (action, currentState, previousState) =>
    userSlice.actions.updateInProgressMessage.match(action) &&
    !!currentState.userSlice.chat.name &&
    currentState.userSlice.chat.name !== previousState.userSlice.chat.name,
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(chatsApi.util.invalidateTags(["Chats"]))
  },
})

// Sync final trip to cache when streaming stops
tripSyncMiddleware.startListening({
  actionCreator: userSlice.actions.stopMessageStreaming,
  effect: (action, listenerApi) => {
    const finalTrip = action.payload.chat.trip

    if (finalTrip?.ref) {
      const currentTripRef = selectTripRef(listenerApi.getState())

      if (currentTripRef === finalTrip.ref) {
        updateTripCache(
          listenerApi.dispatch as AppDispatch,
          finalTrip.ref,
          finalTrip,
          listenerApi.getState
        )
      }
    }
  },
})

tripSyncMiddleware.startListening({
  actionCreator: removeTrip,
  effect: (action) => {
    const tripRef = action.payload
    TripSync.instance.broadcastTripDeleted(tripRef)
  },
})

tripSyncMiddleware.startListening({
  matcher: mediaApi.endpoints.addMediaGuide.matchFulfilled,
  effect: (action) => {
    const { inspirationRef } = action.meta.arg.originalArgs
    if (inspirationRef) {
      TripSync.instance.broadcastMediaTripRefsInvalidated([inspirationRef])
    }
  },
})

tripSyncMiddleware.startListening({
  matcher: mediaApi.endpoints.addMediaCollection.matchFulfilled,
  effect: (action) => {
    const { collectionRef } = action.meta.arg.originalArgs
    if (collectionRef) {
      TripSync.instance.broadcastMediaTripRefsInvalidated([collectionRef])
    }
  },
})

tripSyncMiddleware.startListening({
  matcher: mediaApi.endpoints.removeMedia.matchFulfilled,
  effect: () => {
    // Note: removeMedia doesn't provide sourceRef, so we invalidate all MediaTripRefs
    // This is a broader invalidation but necessary since we don't know the source
    TripSync.instance.broadcastMediaTripRefsInvalidated([])
  },
})

tripSyncMiddleware.startListening({
  matcher: mediaApi.endpoints.removeGuideForTrip.matchFulfilled,
  effect: (action) => {
    const { inspirationRef } = action.meta.arg.originalArgs
    if (inspirationRef) {
      TripSync.instance.broadcastMediaTripRefsInvalidated([inspirationRef])
    }
  },
})

tripSyncMiddleware.startListening({
  matcher: mediaApi.endpoints.removeCollectionForTrip.matchFulfilled,
  effect: (action) => {
    const { collectionRef } = action.meta.arg.originalArgs
    if (collectionRef) {
      TripSync.instance.broadcastMediaTripRefsInvalidated([collectionRef])
    }
  },
})

export { tripSyncMiddleware }
